<template>
  <div
    data-test-id="giftcard-pdf-preview"
    class="rounded-2xl p-5"
    :class="
      expanded
        ? ''
        : 'max-h-[290px] overflow-hidden md:max-h-none md:overflow-visible'
    "
    :style="previewBackground"
  >
    <!-- expanded: zoom instead of scale, so the whole card shrinks to fit
         (scale keeps its unscaled height, which the cropped mode hides) -->
    <div
      class="flex flex-col items-center gap-3"
      :class="
        expanded ? 'expanded-content' : 'origin-top scale-[0.63] md:scale-100'
      "
    >
      <img
        src="/_nuxt3-static/logos/logo.svg"
        :alt="t('personalization.preview.brand')"
        class="h-5"
      />

      <div class="preview-panel relative isolate w-full">
        <div
          data-test-id="giftcard-preview-card"
          class="m-2 p-4 rounded-2xl shadow-md"
          :style="surfaceBackground"
        >
          <div class="mb-4 flex items-center justify-between gap-2">
            <span
              class="rounded-sm bg-teal-900 px-2 py-1 text-base font-bold text-white"
            >
              {{ t('personalization.preview.badge') }}
            </span>
            <div class="cutout" :style="cutoutBackground"></div>
            <span
              data-test-id="giftcard-preview-amount"
              class="text-h6 font-bold text-teal-900"
            >
              {{
                amount === null
                  ? t('personalization.noAmount')
                  : formatMoney(amount, locale, 'EUR', 2)
              }}
            </span>
          </div>

          <div class="mb-3 flex w-full h-24 md:h-28 rounded-2xl gap-[2px]">
            <NuxtImg
              src="/_nuxt3-static/giftcard/personalization-header-1.png"
              alt=""
              class="w-1/3 object-cover rounded-l-2xl"
            />
            <NuxtImg
              src="/_nuxt3-static/giftcard/personalization-header-2.png"
              alt=""
              class="w-1/3 object-cover"
            />
            <NuxtImg
              src="/_nuxt3-static/giftcard/personalization-header-3.png"
              alt=""
              class="w-1/3 object-cover rounded-r-2xl"
            />
          </div>

          <h3
            data-test-id="giftcard-preview-title"
            class="mb-2 text-xl font-bold text-teal-900 text-center"
          >
            {{
              title ||
              t('personalization.preview.title', {
                design: designId
                  ? t(`personalization.design.${designId.toLowerCase()}`)
                  : '',
              })
            }}
          </h3>
          <p
            data-test-id="giftcard-preview-message"
            class="mb-1 text-xs text-gray-700"
          >
            {{
              message ||
              t('personalization.preview.description', { eventCount })
            }}
          </p>

          <div class="my-4 rounded-2xl bg-white p-4 mx-6">
            <p class="mb-1 text-overline font-semibold text-gray-500">
              {{ t('personalization.delivery.fromLabel') }}
            </p>
            <p
              data-test-id="giftcard-preview-from"
              class="mb-3 h-9 flex items-center rounded-lg bg-gray-100 px-3 text-sm text-gray-900"
            >
              {{ buyerName }}
            </p>
            <p class="mb-1 text-overline font-semibold text-gray-500">
              {{ t('personalization.delivery.toLabel') }}
            </p>
            <p
              data-test-id="giftcard-preview-to"
              class="h-9 flex items-center rounded-lg bg-gray-100 px-3 text-sm text-gray-900"
            >
              {{ recipientName }}
            </p>
          </div>

          <div
            class="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs text-gray-600"
          >
            <span data-test-id="giftcard-preview-valid-until">
              {{ t('personalization.preview.validUntil') }}:
              {{ validUntilDate }}
            </span>
            <span>{{ t('personalization.preview.code') }}: XXXXXXXX</span>
          </div>
        </div>

        <h4 class="mt-2 text-center text-base font-bold text-teal-900">
          {{ t('personalization.preview.redeemTitle') }}
        </h4>

        <div
          class="grid grid-cols-3 rounded-2xl px-2 py-2 text-center text-overline m-2"
          :style="surfaceBackground"
        >
          <div
            v-for="(step, index) in redeemSteps"
            :key="step"
            class="px-2"
            :class="index > 0 ? 'border-l border-gray-300 border-dashed' : ''"
          >
            <p class="mb-1 font-bold text-teal-900 text-caption-small">
              {{ index + 1 }}
            </p>
            <p class="text-gray-700">{{ step }}</p>
          </div>
        </div>
      </div>

      <QrcodeSvg
        value="https://gokonfetti.com"
        :size="64"
        level="M"
        class="mt-2 border-2 border-white"
      />
      <p class="text-caption-small italic text-gray-500">
        {{ t('personalization.preview.qrCaption') }}
      </p>

      <p class="text-center text-caption-small text-gray-500">
        {{ t('personalization.preview.footer', { eventCount }) }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { QrcodeSvg } from 'qrcode.vue'
import { NUMBER_OF_EVENTS } from '~/composables/product/constants'
import {
  GIFTCARD_DESIGNS,
  GIFTCARD_PREVIEW_FALLBACK_BACKGROUND,
  type GiftcardDesignId,
} from '~/utils/giftcardDesigns'

const props = defineProps<{
  /** null = no amount chosen yet, shown as a placeholder */
  amount: number | null
  designId?: GiftcardDesignId
  /** overrides the built-in design's background (themed giftcards) */
  background?: { image?: string; color?: string }
  /** override the default PDF title / description copy */
  title?: string
  message?: string
  buyerName?: string
  recipientName?: string
  /** show the whole card, scaled to fit, instead of the cropped mobile view */
  expanded?: boolean
}>()

const { t, locale } = useI18n()

const design = computed(() =>
  GIFTCARD_DESIGNS.find((item) => item.id === props.designId)
)

const previewBackground = computed(() => {
  const image = props.background
    ? props.background.image
    : design.value?.backgroundImage
  const color = props.background?.color ?? GIFTCARD_PREVIEW_FALLBACK_BACKGROUND
  return image
    ? {
        backgroundImage: `url(${image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'top',
        backgroundColor: color,
      }
    : { backgroundColor: color }
})

// solid colour of the card surfaces (Von/Für panel, redeem steps), matching
// the real PDF: blue for every design except GRADIENT, which stays beige
const previewSurfaceColor = computed(
  () =>
    props?.background?.color ??
    design.value?.previewSurfaceColor ??
    GIFTCARD_PREVIEW_FALLBACK_BACKGROUND
)

const surfaceBackground = computed(() => ({
  backgroundColor: previewSurfaceColor.value,
}))

const cutoutBackground = computed(() => ({
  '--bg': `color-mix(in srgb, ${previewSurfaceColor.value} 20%, #f8f4ee)`,
}))

const eventCount = computed(() =>
  formattedNumberOfEvents(locale.value, NUMBER_OF_EVENTS)
)

const validUntilDate = computed(() =>
  dayjs().add(3, 'year').format('DD.MM.YYYY')
)

const redeemSteps = computed(() => [
  t('personalization.preview.redeemStep1'),
  t('personalization.preview.redeemStep2'),
  t('personalization.preview.redeemStep3'),
])
</script>

<style lang="css" scoped>
@reference '~/assets/css/tailwind.css';
/* decorative panel behind the redeem-steps box: isolate gives this element
   its own stacking context, so -z-10 only drops the pseudo-element below its
   own siblings here, not below the card's background image on the ancestor */
.preview-panel::before {
  content: '';
  @apply absolute bottom-0 -z-10 h-5/6 w-full rounded-2xl bg-white;
  @apply shadow;
}

.expanded-content {
  zoom: 0.63;
}

.cutout {
  --bg: #f3f0eb;

  position: relative;
  width: 76px;
  height: 16px;

  background: var(--bg);
  @apply rounded-full;

  box-shadow:
    inset 0 2px 1px rgba(0, 0, 0, 0.16),
    inset 0 -2px 5px rgba(255, 255, 255, 0.7);
}

.cutout::before {
  content: '';
  position: absolute;
  width: 16px;
  height: 14px;
  left: 50%;
  top: -6px;
  transform: translateX(-50%);

  background: var(--bg);
  border-radius: 50%;

  box-shadow:
    inset 0 3px 1px rgba(0, 0, 0, 0.18),
    inset 0 3px 1px rgba(255, 255, 255, 0.7);
}

/* transform:scale() doesn't shrink this box's contribution to its flex
   parent's (.gc-aside) width - it's still laid out (and sized) pre-scale,
   so below 400px it can push the page into horizontal overflow. min-width:0
   lets it actually shrink to the space .gc-aside gives it; the scaled-down
   card only needs a fraction of that, so nothing gets visually clipped. */
@media (max-width: 399px) {
  [data-test-id='giftcard-pdf-preview'] {
    min-width: 0;
  }
}
</style>
